import { subDays } from 'date-fns';
import moment, { Moment } from 'moment';

import { Utils } from '@utils/utils';

import { ContractState, Frequency, ReferenceCondition, Unit } from '../types/all';
import { Commodity } from '../types/commodity';
import { ContractOfferType } from '../types/contract-offer-type';
import { OrchestradeBookingMode } from '../types/contract-ot-state-type';
import { GridConnection } from '../types/grid-connection.enum';
import { ToNominate } from '../types/to-nominate-type.enum';
import { User } from '../user.model';

import { Account } from './account.model';
import { ContractEngagement } from './contract-engagement.model';
import { InstrumentFormula } from './contract-formula-instrument-formula.model';
import { Instrument } from './contract-formula-instrument.model';
import { BrickUnit } from './contract-order-brick.model';
import { ContractOrderIndexModel } from './contract-order-index.model';
import { ContractPerimeter } from './contract-perimeter.model';
import { ContractPriceStrip } from './contract-price-strip.model';
import { ContractSettings } from './contract-settings.model';
import { DeliveryPoint } from './delivery-point.model';
import { ExternalCode } from './external-code.model';
import { MeteorMappingInfo } from './formula-meteor-mapping-info.model';
import { Site } from './site.model';
import { Supplier } from './supplier.model';

export enum TimeHorizonsEnum {
  day = 'day',
  week = 'week',
  month = 'month',
  year = 'year',
  custom = 'custom',
}

export enum TimeCategory {
  past,
  current,
  future,
}

enum Period {
  past = 'past',
  current = 'current',
  future = 'future',
}

/**
 * Model for API ContractHeader Model
 *
 * Properties for patch contract are set manualy in contract-editor-aggregate.
 * If one person want the property is patched with 'contract header'
 * (simple column in contract db, not a large relationship to other table, eg: perimeter, ...),
 * make sure to add it in headearEqualTo() below and add it in manualy in contract-editor-aggregate.service.
 * Otherwise, make sure to call the appropriate endpoint for the property.
 */
export class Contract {
  /**
   * Contract unique id
   */
  id: number;

  /**
   * Contract display name.
   */
  name: string;

  /**
   * Contract’s state
   */
  state: ContractState = ContractState.draft;

  /**
   * Account for this contract
   */
  account: Account;

  /**
   * Trade date.
   */
  tradeDate: Moment;

  /**
   * Contract commodity
   */
  commodity: Commodity;

  /**
   * Contract country of origin.
   */
  country: string;

  /**
   * Contract comment.
   */
  comment: string;

  /**
   * Contract contact.
   */
  contact: User;

  /**
   * Support Account for this contract
   */
  support: User;

  /**
   * Start date.
   */
  startDate: Moment;

  /**
   * End date.
   */
  endDate: Moment;

  /**
   * Engagements.
   */
  engagements: ContractEngagement[] = [];

  /**
   * Contract perimeter(s).
   */
  perimeters: ContractPerimeter[];

  /**
   * Internal supplier entity / sub-entity pair.
   */
  supplier: Supplier = { id: 0, supplierEntity: '', supplierSubEntity: '' };

  /**
   * External contract's code
   */
  externalCode: ExternalCode;

  /**
   * Offer's name
   */
  offerName: string;

  offerType: ContractOfferType;

  orchestratededBookingMode: OrchestradeBookingMode = OrchestradeBookingMode.noOTBooking;

  dstManagement: Frequency;

  /**
   * Contract settings
   */
  contractSettings: ContractSettings;

  /**
   * Contract indexes
   */
  indexes?: ContractOrderIndexModel[] = [];

  private _nominationDate?: Date;

  constructor(attributes: Partial<Contract> = {}) {
    Object.assign(this, attributes);
  }

  equalTo(other: Contract) {
    return other.id === this.id;
  }

  toString() {
    return this.name;
  }

  get nominationEndDate(): Moment {
    return this.endDate.clone().subtract(1, 'days');
  }

  get nominationStartDate(): Moment {
    return this.startDate.clone().subtract(1, 'days').set({
      hour: 10,
      minute: 30,
      second: 0,
      millisecond: 0,
    });
  }

  get nominationDate() {
    return this._nominationDate ? this._nominationDate : this.isFuture() ? subDays(this.nominationStartDate.toDate(), 1) : new Date();
  }

  set nominationDate(value: Date) {
    this._nominationDate = value;
  }

  get toNominate(): boolean {
    return this.perimeters.some((p) => p.points.some((pt) => pt.isNominated));
  }

  get isB2BItalianGasContract(): boolean {
    return (
      this.supplier.supplierEntity === 'ENGIE Italia S.p.A.' &&
      this.supplier.supplierSubEntity === 'B2B' &&
      this.country === 'IT' &&
      this.commodity === Commodity.gas
    );
  }

  get isUpStream(): boolean {
    if (this.commodity === Commodity.gas) return false;
    const cond1 = this.supplier.supplierSubEntity === 'Global Energy Upstream';
    const cond2 = this.supplier.supplierSubEntity === 'Global Energy' && this.country === 'DE';
    return cond1 || cond2;
  }

  isPast() {
    return this.endDate.isBefore(moment());
  }

  isFuture() {
    return this.nominationStartDate.isAfter(moment());
  }

  currencySymbol(orderCurrency: string = 'EUR'): string {
    const currencySymbol = Utils.getCurrencySymbol(orderCurrency);
    return this.isB2BItalianGasContract ? `c${currencySymbol}` : `${currencySymbol}`;
  }

  currencySymbolUnit(operator: '*' | '+' = '+', orderCurrency: string = 'EUR'): string {
    if (operator === '*') return '%';
    const currencySymbol = this.currencySymbol(orderCurrency);
    return `${currencySymbol}/${this.isB2BItalianGasContract ? Unit.Nm3 : Unit.MWh}`;
  }

  dimension(unit?: Unit) {
    if (this.commodity === Commodity.gas) {
      if (this.isB2BItalianGasContract) return 'gas_normalized_volume';
      return unit === Unit.MWh ? 'gas_energy' : 'gas_power';
    }
    return unit === Unit.MWh ? 'power_energy' : 'power_power';
  }

  nominationUnit(unit?: Unit) {
    if (this.commodity === 'power') {
      return this.country === 'IT' ? 'kWh' : 'MWh';
    }
    return unit;
  }

  get calculationUnits(): { key: string; value: string }[] {
    return !this.isB2BItalianGasContract
      ? [
          { key: 'mwh_month', value: 'MWh' },
          { key: 'mwh_day', value: 'MWh/day' },
          { key: 'mw', value: 'MW' },
        ]
      : [{ key: 'Nm3', value: 'Nm3' }];
  }

  get gasUnitOptions(): Unit[] {
    if (this.isB2BItalianGasContract) {
      return [Unit.Nm3];
    }
    return [Unit.MWh];
  }

  get brickUnit() {
    return this.isB2BItalianGasContract ? Unit.Nm3 : Unit.MWh;
  }

  get unitOptions() {
    return this.isB2BItalianGasContract ? [Unit.Nm3, '%'] : [Unit.MWh, Unit.MW, '%'];
  }

  get unitsOptions() {
    return this.isB2BItalianGasContract ? [Unit.Nm3, '%'] : [Unit.MWh, 'MWh/day', Unit.MW, '%'];
  }

  get brickOperators(): { value: string; display: string }[] {
    return [
      { value: '+', display: this.currencySymbolUnit() },
      { value: '*', display: this.currencySymbolUnit('*') },
    ];
  }

  get getBrickUnitJson(): BrickUnit {
    return { dimension: this.dimension(Unit.MWh), unit: this.brickUnit, refConditions: this.getReferenceCondition() };
  }

  getUnitJson(unit?: Unit): string | undefined {
    if (this.commodity === Commodity.gas) {
      return JSON.stringify({ unit, dimension: this.dimension(unit), ref_conditions: this.getReferenceCondition() });
    }
    return JSON.stringify({ unit, dimension: this.dimension(unit) });
  }

  getAllDeliveryPoints(): DeliveryPoint[] {
    let points: DeliveryPoint[] = [];
    for (const perimeter of this.perimeters || []) {
      points = points.concat(this.getDeliveryPointsWithPerimeter(perimeter));
    }
    return points;
  }

  getUniqueDeliveryPoints(): DeliveryPoint[] {
    return Utils.unique(this.getAllDeliveryPoints(), (p: DeliveryPoint) => p.toString());
  }

  getDeliveryPointsToNominateWithGridConnection() {
    const pointsGroupByMeteringAndGrid = this.getAllDeliveryPoints()
      .filter((point: DeliveryPoint) => [ToNominate.indicative, ToNominate.mandatory].includes(point.toNominate))
      .reduce((acc, current: DeliveryPoint) => {
        acc[current.toString()] = acc[current.toString()] ?? [];
        acc[current.toString()].push(current);
        return acc;
      }, {});

    return Object.values(pointsGroupByMeteringAndGrid).map((deliveryPoints: DeliveryPoint[]) => {
      const uniqueDpPerGridConnection = Utils.unique(
        deliveryPoints,
        (point: DeliveryPoint) => point.toString() + point.perimeter?.gridConnection,
      );
      uniqueDpPerGridConnection[0].associatedGridConnections = [];
      uniqueDpPerGridConnection.forEach((point) => {
        if ([GridConnection.INJECTION, GridConnection.OFFTAKE].includes(point.perimeter?.gridConnection)) {
          uniqueDpPerGridConnection[0].associatedGridConnections.push(point.perimeter?.gridConnection);
        }
      });
      return uniqueDpPerGridConnection[0];
    });
  }

  getDeliveryPointsToNominateV2(sites: Site[] | Site): DeliveryPoint[] {
    const focusSites = Array.isArray(sites) ? sites : [sites];
    const pointSites: DeliveryPoint[] = [];
    focusSites.filter((site) => site.contractIds.includes(this.id)).forEach((cs) => cs.points.forEach((p) => pointSites.push(p)));
    const points: DeliveryPoint[] = [];
    this.perimeters
      .filter((perimeter) => perimeter.points?.length > 0)
      .forEach((perimeter) => {
        perimeter.points
          .map((point) => new DeliveryPoint({ ...point, associatedGridConnections: [] }))
          .forEach((point) => {
            point.associatedGridConnections.push(perimeter.gridConnection);
            point.perimeter = new ContractPerimeter({ id: perimeter.id });
            const siteDeliveryPoint = pointSites.find((deliveryPoint: DeliveryPoint) => deliveryPoint.poorlyEqualTo(point));
            if (siteDeliveryPoint) {
              point.msbNumber = siteDeliveryPoint.msbNumber;
              point.meteringPointOperator = siteDeliveryPoint.meteringPointOperator;
              point.marketArea = siteDeliveryPoint.marketArea;
              point.country = siteDeliveryPoint.country;
              point.site = siteDeliveryPoint.site;
              point.siteId = siteDeliveryPoint.siteId;
            }
            if (
              !points.find((p) => p.meteringPoint === point.meteringPoint && p.associatedGridConnections.includes(perimeter.gridConnection))
            ) {
              points.push(point);
            }
          });
      });
    return points.sort((a, b) => Utils.genericComparer(a.meteringPoint, b.meteringPoint, 'asc'));
  }

  getPriceStrips(): ContractPriceStrip[] {
    let priceStrips: ContractPriceStrip[] = [];
    for (const perimeter of this.perimeters || []) {
      priceStrips = priceStrips.concat(perimeter.priceStrips || []);
    }

    return priceStrips;
  }

  getSitesId(): number[] {
    return Array.from(this.getUniqueDeliveryPoints().reduce((acc, point: DeliveryPoint) => acc.add(point.siteId), new Set())) as number[];
  }

  getMeteorMappingInfosSet(): MeteorMappingInfo[] {
    const infoList: MeteorMappingInfo[] = [];
    this.getPriceStrips().forEach((ps) => {
      ps.instruments.forEach((instrument: Instrument) => {
        instrument.buyBackFormulas.forEach((formula: InstrumentFormula) => {
          if (formula.meteorMappingInfo) {
            infoList.push(formula.meteorMappingInfo);
          }
        });
        instrument.fixingPriceFormulas.forEach((formula: InstrumentFormula) => {
          if (formula.meteorMappingInfo) {
            infoList.push(formula.meteorMappingInfo);
          }
        });
      });
    });
    return [...new Map(infoList.map((info) => [info.toString(), info])).values()];
  }

  /**
   * no ref condition for contract power
   * all countries are XX_metering except Italy which takes IT_MARKET
   */
  getReferenceCondition(): ReferenceCondition {
    switch (this.country) {
      case 'IT':
        return ReferenceCondition.IT_METERING;
      case 'FR':
        return ReferenceCondition.FR_METERING;
      case 'BE':
        return ReferenceCondition.BE_METERING;
      case 'DE':
        return ReferenceCondition.DE_METERING;
      case 'ES':
        return ReferenceCondition.ES_METERING;
      case 'GB':
        return ReferenceCondition.GB_METERING;
      case 'NL':
        return ReferenceCondition.NL_METERING;
      case 'TR':
        return ReferenceCondition.TR_METERING;
      case 'CZ':
        return ReferenceCondition.CZ_METERING;
    }
  }

  buildUserCurveUnit(timeHorizon?: TimeHorizonsEnum): {
    unit: Unit;
    dimension: string;
    ref_conditions: ReferenceCondition;
  } {
    let unit = Unit.MWh;
    let dimension = 'gas_energy';
    const ref_conditions = this.commodity === 'gas' ? this.getReferenceCondition() : undefined;
    if (this.commodity === 'power') {
      dimension = timeHorizon === TimeHorizonsEnum.day ? 'power_power' : 'power_energy';
      unit = timeHorizon === TimeHorizonsEnum.day ? Unit.MW : Unit.MWh;
      if (this.country === 'IT') unit = timeHorizon === TimeHorizonsEnum.day ? Unit.kW : Unit.kWh;
    }
    return { dimension, unit, ref_conditions };
  }

  getUserCurveTag(direction: GridConnection, is15T: boolean): string {
    switch (this.commodity) {
      case Commodity.gas:
        return is15T ? 'offtake' : 'forecast_offtake';
      case Commodity.power:
        return direction === GridConnection.INJECTION
          ? is15T
            ? 'injection'
            : 'forecast_injection'
          : is15T
            ? 'offtake'
            : 'forecast_offtake';
    }
  }

  countryIn(values: string[]) {
    return values.some((value) => this.country === value);
  }

  getContractPeriod(): Period {
    const now = moment();
    if (this.endDate.isBefore(now)) {
      return Period.past;
    }
    if (this.startDate.isAfter(now)) {
      return Period.future;
    }
    return Period.current;
  }

  isEligibleForAuction(): boolean {
    if (!this.perimeters) return false;
    return this.perimeters.some((p) => p.offerType === ContractOfferType.buildAndOptimize);
  }

  getTimeCategories(now: Moment): TimeCategory {
    return this.endDate.isBefore(now) ? TimeCategory.past : this.startDate.isBefore(now) ? TimeCategory.current : TimeCategory.future;
  }

  /* eslint-disable complexity */
  headearEqualTo(otherContract: Contract): boolean {
    return (
      this.orchestratededBookingMode === otherContract.orchestratededBookingMode &&
      this.commodity === otherContract.commodity &&
      this.name === otherContract.name &&
      this.supplier?.supplierEntity === otherContract.supplier?.supplierEntity &&
      this.supplier?.supplierSubEntity === otherContract.supplier?.supplierSubEntity &&
      this.country === otherContract.country &&
      this.startDate === otherContract.startDate &&
      this.endDate === otherContract.endDate &&
      this.tradeDate === otherContract.tradeDate &&
      this.contact?.id === otherContract.contact?.id &&
      this.support?.id === otherContract.support?.id &&
      this.dstManagement === otherContract.dstManagement
    );
  }

  setPriceStripUnitJson() {
    const unitJson = this.isB2BItalianGasContract ? this.getUnitJson(Unit.Nm3) : undefined;
    this.perimeters = this.perimeters.map((perimeter: ContractPerimeter) => {
      return new ContractPerimeter({
        ...perimeter,
        priceStrips: (perimeter.priceStrips || []).map((priceStrip: ContractPriceStrip) => {
          return new ContractPriceStrip({
            ...priceStrip,
            pricingCurvesUnit: unitJson,
            invoicingCurvesUnit: unitJson,
          });
        }),
      });
    });
  }

  /* eslint-enable complexity */

  private getDeliveryPointsWithPerimeter(perimeter: ContractPerimeter): DeliveryPoint[] {
    return perimeter.points
      ? perimeter.points.map((point) => {
          point.contract.id = this.id;
          point.perimeter = new ContractPerimeter(perimeter);
          return point;
        })
      : [];
  }
}
